import React from "react";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { IconChevronLeft, IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import DebouncedInput from "components/DebounceInput";
import LoadingIcon from "components/LoadingIcon";
import useOutsideClick from "hooks/useOutsideClick";
import { PropertyBrowserNodeOUT } from "api-client";
import { usePropertyBrowserStore } from "./PropertyBrowserStoreContext";
import { useNodeLabel } from "./PropertyBrowserNodeRow";
import PropertyBrowserNodeRow from "./PropertyBrowserNodeRow";
import PropertyBrowserSearchResult from "./PropertyBrowserSearchResult";
import PropertyBrowserScrollTrigger from "./PropertyBrowserScrollTrigger";

export { PropertyBrowserStoreContext } from "./PropertyBrowserStoreContext";

export interface PropertyBrowserTreeProps {
  onSelect: (node: PropertyBrowserNodeOUT) => void;
  isOpen?: (open: boolean) => void;
  titleOrIcon?: React.ReactNode;
  /** Prevents opening the dropdown while still allowing pointer-events inside titleOrIcon (e.g. the X clear button). */
  disableOpen?: boolean;
  containerClassName?: string;
  optionsClassName?: string;
  filter?: (node: PropertyBrowserNodeOUT) => boolean;
  /**
   * Pins this browser instance to a subtree. The browser opens inside this
   * group, the back button is hidden at that level, and search is scoped to
   * this subtree.
   */
  rootGroupId?: string;
  /** Show the search input. Defaults to true. */
  showSearch?: boolean;
}

const PropertyBrowserTree = observer(function PropertyBrowserTree(
  props: PropertyBrowserTreeProps
) {
  const store = usePropertyBrowserStore();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [navStack, setNavStack] = React.useState<PropertyBrowserNodeOUT[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const currentGroup = navStack.at(-1);
  const currentParentId = currentGroup?.node_id ?? null;
  const currentPage = store.nodeChildren.get(currentParentId);
  const currentGroupLabel = useNodeLabel(currentGroup);
  const isSearching = Boolean(store.searchQuery);
  const canGoBack = props.rootGroupId
    ? navStack.length > 1
    : navStack.length > 0;

  // Derived in the render body so MobX tracks the nodeChildren read inside the
  // observer wrapper — moving this into a useEffect would break the late-load case.
  const rootNode = props.rootGroupId
    ? store.nodeChildren
        .get(null)
        ?.items.find((n) => n.node_id === props.rootGroupId)
    : undefined;

  useOutsideClick(containerRef, () => setOpen(false));

  React.useEffect(() => {
    props.isOpen?.(open);
    if (!open) {
      setNavStack([]);
      store.search("");
    }
  }, [open]);

  // Fetch children for the current level whenever the visible group changes.
  React.useEffect(() => {
    if (
      open &&
      !store.nodeChildren.has(currentParentId) &&
      !store.loadingNodes.has(currentParentId)
    ) {
      store.fetchChildren(currentParentId);
    }
  }, [open, currentParentId, store]);

  // Single owner of navStack initialisation: runs on open and whenever rootNode
  // resolves (covers both the data-ready and late-load cases).
  React.useEffect(() => {
    if (open && rootNode && navStack.length === 0) {
      if (!store.nodeChildren.has(rootNode.node_id)) {
        store.fetchChildren(rootNode.node_id);
      }
      setNavStack([rootNode]);
    }
  }, [open, rootNode, store, navStack]);

  const handleOpen = () => {
    setOpen((v) => !v);
  };

  const handleSelect = (node: PropertyBrowserNodeOUT) => {
    props.onSelect(node);
    setOpen(false);
  };

  const handleNavigate = (group: PropertyBrowserNodeOUT) => {
    if (!store.nodeChildren.has(group.node_id)) {
      store.fetchChildren(group.node_id);
    }
    setNavStack((prev) => [...prev, group]);
  };

  const handleSearchChange = (value: string | number) => {
    const query = String(value);
    if (query) {
      setNavStack([]);
    } else {
      setNavStack(rootNode ? [rootNode] : []);
    }
    store.search(query, props.rootGroupId);
  };

  return (
    <div
      ref={containerRef}
      className={clsx("relative", props.containerClassName)}
    >
      <div
        className={clsx("flex items-center w-full", {
          "pointer-events-none": props.disableOpen,
        })}
        onClick={() => !props.disableOpen && handleOpen()}
      >
        <div className="pointer-events-auto w-full">{props.titleOrIcon}</div>
      </div>

      {open && (
        <div
          className={clsx(
            "absolute z-10 mt-1 rounded-md bg-white shadow-lg border border-gray-300",
            "w-80 max-h-[396px] flex flex-col overflow-hidden",
            props.optionsClassName
          )}
        >
          {props.showSearch !== false && (
            <div className="sticky top-0 p-4 z-10 bg-white">
              <div className="bg-white border-gray-300 border rounded-md flex justify-between px-2 items-center text-gray-900">
                <DebouncedInput
                  value={store.searchQuery}
                  onChange={handleSearchChange}
                  debounce={300}
                  placeholder={t("commons.search", "Search…")}
                  className="w-full pl-0 placeholder:text-gray-500 text-sm leading-5"
                  containerClassName="w-full"
                  inputWrapperClassName="border-none"
                  showIcon={false}
                />
                <IconSearch width={24} className="text-gray-500 shrink-0" />
              </div>
            </div>
          )}

          {!isSearching && currentGroupLabel && (
            <div
              className={clsx(
                "text-gray-500 flex items-center pl-2 pr-4 py-[6px] border-y border-gray-300 bg-gray-50",
                canGoBack && "cursor-pointer hover:bg-gray-100"
              )}
              onClick={() =>
                canGoBack && setNavStack((prev) => prev.slice(0, -1))
              }
            >
              {canGoBack && (
                <IconChevronLeft
                  size={24}
                  className="shrink-0 hover:text-indigo-700"
                />
              )}
              <div className="text-sm font-semibold w-full">
                {currentGroupLabel}
              </div>
            </div>
          )}

          <div className="overflow-y-auto flex-1 py-1">
            {isSearching ? (
              <SearchContent
                loading={store.searchLoading}
                tooGeneral={store.searchTooGeneral}
                results={store.searchResults}
                onSelect={handleSelect}
                rootGroupId={props.rootGroupId}
              />
            ) : !currentPage ? (
              <div className="flex justify-center py-4">
                <LoadingIcon type="primary" />
              </div>
            ) : currentPage.items.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-4">
                {t("commons.onDropDownNoSearchResult", "No results found")}
              </div>
            ) : (
              <>
                {currentPage.items.map((node) => (
                  <PropertyBrowserNodeRow
                    key={node.node_id}
                    node={node}
                    onNavigate={handleNavigate}
                    onSelect={handleSelect}
                    filter={props.filter}
                  />
                ))}
                {currentPage.hasMore && (
                  <PropertyBrowserScrollTrigger
                    parentNodeId={currentParentId}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

function SearchContent({
  loading,
  tooGeneral,
  results,
  onSelect,
  rootGroupId,
}: {
  loading: boolean;
  tooGeneral: boolean;
  results: PropertyBrowserNodeOUT[];
  onSelect: (node: PropertyBrowserNodeOUT) => void;
  rootGroupId?: string;
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingIcon type="primary" />
      </div>
    );
  }
  if (tooGeneral) {
    return (
      <div className="text-sm text-gray-400 text-center py-4 px-4">
        {t("propertyBrowser.searchTooGeneral")}
      </div>
    );
  }
  if (results.length === 0) {
    return (
      <div className="text-sm text-gray-400 text-center py-4">
        {t("commons.onDropDownNoSearchResult")}
      </div>
    );
  }
  return (
    <>
      {results.map((node) => (
        <PropertyBrowserSearchResult
          key={node.node_id}
          node={node}
          onSelect={onSelect}
          rootGroupId={rootGroupId}
        />
      ))}
    </>
  );
}

export default PropertyBrowserTree;
